/**
 * AI Service
 * Centralized service for all AI-powered features
 */

import ApiService from '../api.service';
import {
  AIServiceStatus,
  TestGenerationRequest,
  TestGenerationResponse,
  DataSuggestionRequest,
  DataSuggestionResponse,
  PageContext,
  LegacyAIRequest,
  FieldDefinition,
  DataCategory,
  WebElement,
  EnhancedLabelResult,
  ElementAnalysisResult,
  TestCaseSummaryForGrouping,
  SuiteGroupingResponse,
  GenerateTestComponentsRequest,
  GenerateTestComponentsResponse,
  FlowGenerationRequest,
  FlowGenerationResponse,
  ManualToAutomationRequest,
  ManualToAutomationResponse,
  ComponentSummary,
  ApiTestDataGenerationRequest,
  ApiTestDataGenerationResponse,
  RefineValidationsRequest,
  RefineValidationsResponse,
  SelectorDisambiguationRequest,
  SelectorDisambiguationResponse,
  TestPlanGenerationRequest,
  TestPlanGenerationResponse,
  SaveTestPlanRequest,
  SaveTestPlanResponse
} from './ai.types';
import logger from '../../utils/logger';

class AIService {
  private static instance: AIService;
  private api: ApiService;
  private cachedStatus: AIServiceStatus | null = null;
  private statusCacheTime: number = 0;
  private readonly STATUS_CACHE_TTL = 60000; // 1 minute cache

  private constructor() {
    this.api = ApiService.getInstance();
  }

  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  // ============================================================================
  // Status & Health
  // ============================================================================

  /**
   * Get AI service status including feature flags
   */
  async getStatus(forceRefresh: boolean = false): Promise<AIServiceStatus | null> {
    // Return cached status if valid
    if (!forceRefresh && this.cachedStatus && Date.now() - this.statusCacheTime < this.STATUS_CACHE_TTL) {
      return this.cachedStatus;
    }

    try {
      const response = await this.api.get<AIServiceStatus>('/api/ai/status');
      if (response && response.success !== false) {
        this.cachedStatus = response as unknown as AIServiceStatus;
        this.statusCacheTime = Date.now();
        console.log('[AIService] Status loaded — aiEnabled:', this.cachedStatus?.aiEnabled,
          'localLLM:', this.cachedStatus?.localLLM?.enabled,
          'features:', JSON.stringify(this.cachedStatus?.localLLM?.features));
        return this.cachedStatus;
      }
      return null;
    } catch (error) {
      logger.error('AI_SERVICE', 'Failed to get AI status', error);
      return null;
    }
  }

  /**
   * Check if any AI feature is available
   */
  async isAIAvailable(): Promise<boolean> {
    const status = await this.getStatus();
    return status?.aiEnabled || false;
  }

  /**
   * Check if a specific feature is enabled
   */
  async isFeatureEnabled(feature: keyof AIServiceStatus['localLLM']['features']): Promise<boolean> {
    const status = await this.getStatus();
    if (!status?.aiEnabled) return false;
    if (status.localLLM?.enabled && status.localLLM.features?.[feature]) return true;
    return false;
  }

  // ============================================================================
  // Test Generation
  // ============================================================================

  /**
   * Generate test cases from captured page elements
   */
  async generateTestCases(request: TestGenerationRequest): Promise<TestGenerationResponse> {
    try {
      logger.info('AI_SERVICE', 'Generating test cases', { url: request.pageContext.url });

      const response = await this.api.post<TestGenerationResponse>(
        '/api/ai/test-generation/generate',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Test cases generated', {
          count: (response as any).testCases?.length || 0
        });
        return response as unknown as TestGenerationResponse;
      }

      return {
        requestId: '',
        testCases: [],
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to generate test cases'
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Test generation failed', error);
      return {
        requestId: '',
        testCases: [],
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate API test cases from OpenAPI spec
   */
  async generateAPITests(apiSpec: Record<string, any>): Promise<TestGenerationResponse> {
    try {
      const response = await this.api.post<TestGenerationResponse>(
        '/api/ai/test-generation/api',
        apiSpec
      );

      return response as unknown as TestGenerationResponse || {
        requestId: '',
        testCases: [],
        success: false,
        errorMessage: 'Failed to generate API tests'
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'API test generation failed', error);
      return {
        requestId: '',
        testCases: [],
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ============================================================================
  // Data Suggestion
  // ============================================================================

  /**
   * Get test data suggestions for form fields
   */
  async suggestTestData(request: DataSuggestionRequest): Promise<DataSuggestionResponse> {
    try {
      logger.info('AI_SERVICE', 'Getting data suggestions', { fieldCount: request.fields.length });

      const response = await this.api.post<DataSuggestionResponse>(
        '/api/ai/data-suggestion/suggest',
        request
      );

      return response as unknown as DataSuggestionResponse || {
        requestId: '',
        fieldSuggestions: [],
        success: false,
        errorMessage: 'Failed to get data suggestions'
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Data suggestion failed', error);
      return {
        requestId: '',
        fieldSuggestions: [],
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Quick helper to get suggestions for a single field
   */
  async suggestDataForField(
    fieldName: string,
    dataType: FieldDefinition['dataType'],
    categories: DataCategory[] = ['positive', 'negative']
  ): Promise<DataSuggestionResponse> {
    return this.suggestTestData({
      fields: [{ fieldName, dataType }],
      categories,
      countPerCategory: 5
    });
  }

  // ============================================================================
  // Web Analysis
  // ============================================================================

  /**
   * Analyze page elements for testability
   */
  async analyzePageElements(pageContext: PageContext): Promise<Record<string, any>> {
    try {
      const response = await this.api.post<Record<string, any>>(
        '/api/ai/web-analysis/analyze',
        pageContext
      );

      return response as unknown as Record<string, any> || { success: false };
    } catch (error) {
      logger.error('AI_SERVICE', 'Page analysis failed', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // ============================================================================
  // Legacy API Support (for backward compatibility)
  // ============================================================================

  /**
   * Get valid test data (legacy API)
   */
  async getValidData(request: LegacyAIRequest): Promise<string> {
    try {
      const response = await this.api.post<string>('/api/ai/data/valid', request);
      return response as unknown as string || '';
    } catch (error) {
      logger.error('AI_SERVICE', 'Get valid data failed', error);
      return '';
    }
  }

  /**
   * Get invalid test data (legacy API)
   */
  async getInvalidData(request: LegacyAIRequest): Promise<string> {
    try {
      const response = await this.api.post<string>('/api/ai/data/invalid', request);
      return response as unknown as string || '';
    } catch (error) {
      logger.error('AI_SERVICE', 'Get invalid data failed', error);
      return '';
    }
  }

  // ============================================================================
  // Test Component Generation
  // ============================================================================

  /**
   * Generate standalone test components from a source component's page elements.
   * Creates separate components per test category with AI-generated DataMatrix.
   */
  async generateTestComponents(
    request: GenerateTestComponentsRequest
  ): Promise<GenerateTestComponentsResponse> {
    try {
      logger.info('AI_SERVICE', 'Generating test components', {
        sourceComponentId: request.sourceComponentId,
        categories: request.categories
      });

      const response = await this.api.post<GenerateTestComponentsResponse>(
        '/api/ai/test-generation/generate-components',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Test components generated', {
          count: (response as any).generatedComponents?.length || 0
        });
        return response as unknown as GenerateTestComponentsResponse;
      }

      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to generate test components'
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Test component generation failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ============================================================================
  // Suite Grouping
  // ============================================================================

  async suggestSuiteGroupings(
    projectKey: string,
    testCases: TestCaseSummaryForGrouping[]
  ): Promise<SuiteGroupingResponse> {
    try {
      logger.info('AI_SERVICE', 'Requesting suite grouping suggestions', { count: testCases.length });

      const response = await this.api.post<SuiteGroupingResponse>(
        '/api/ai/test-analysis/suggest-suites',
        { projectKey, testCases }
      );

      if (response && (response as any).success !== false) {
        return response as unknown as SuiteGroupingResponse;
      }

      return {
        suggestions: [],
        ungroupedTestCaseIds: testCases.map(tc => tc.id),
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to get suite grouping suggestions'
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Suite grouping failed', error);
      return {
        suggestions: [],
        ungroupedTestCaseIds: testCases.map(tc => tc.id),
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ============================================================================
  // Flow Generation
  // ============================================================================

  /**
   * Generate automation flows from imported test cases.
   * AI analyzes steps, matches to existing components, creates skeletons,
   * and builds flows linked back to test cases.
   */
  async generateFlowsFromTestCases(
    request: FlowGenerationRequest
  ): Promise<FlowGenerationResponse> {
    try {
      console.log('[AIService] generateFlowsFromTestCases request:', JSON.stringify(request, null, 2));
      logger.info('AI_SERVICE', 'Generating flows from test cases', {
        testCaseCount: request.testCaseIds.length
      });

      const response = await this.api.post<FlowGenerationResponse>(
        '/api/ai/flow-generation/generate',
        request
      );

      console.log('[AIService] generateFlowsFromTestCases raw response:', JSON.stringify(response, null, 2));

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Flows generated', {
          flowCount: (response as any).generatedFlows?.length || 0
        });
        return response as unknown as FlowGenerationResponse;
      }

      console.warn('[AIService] generateFlowsFromTestCases returned failure:', (response as any)?.errorMessage);
      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to generate flows'
      };
    } catch (error) {
      console.error('[AIService] generateFlowsFromTestCases threw error:', error);
      logger.error('AI_SERVICE', 'Flow generation failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ============================================================================
  // Manual-to-Automation
  // ============================================================================

  /**
   * Convert manual TCM test cases into a fully configured automation component
   * using a source component's real page element locators.
   */
  async generateAutomationFromTestCases(
    request: ManualToAutomationRequest
  ): Promise<ManualToAutomationResponse> {
    try {
      console.log('[AIService] generateAutomationFromTestCases request:', JSON.stringify(request, null, 2));
      logger.info('AI_SERVICE', 'Generating automation from test cases', {
        sourceComponentId: request.sourceComponentId,
        testCaseCount: request.testCaseIds.length
      });

      const response = await this.api.post<ManualToAutomationResponse>(
        '/api/ai/manual-to-automation/generate',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Automation generated', {
          componentId: (response as any).componentId,
          matchRate: (response as any).matchRate
        });
        return response as unknown as ManualToAutomationResponse;
      }

      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to generate automation',
        totalSteps: 0, matchedSteps: 0, unmatchedSteps: 0,
        dataRowCount: 0, activityCount: 0, matchRate: 0
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Manual-to-automation failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        totalSteps: 0, matchedSteps: 0, unmatchedSteps: 0,
        dataRowCount: 0, activityCount: 0, matchRate: 0
      };
    }
  }

  /**
   * Fetch components with page elements for the source component picker.
   */
  async getComponentsWithPageElements(projectKey: string): Promise<ComponentSummary[]> {
    try {
      const response = await this.api.get<ComponentSummary[]>(
        `/api/ai/manual-to-automation/source-components?projectKey=${encodeURIComponent(projectKey)}`
      );
      return (response as unknown as ComponentSummary[]) || [];
    } catch (error) {
      logger.error('AI_SERVICE', 'Failed to fetch source components', error);
      return [];
    }
  }

  // ============================================================================
  // API Test Data Generation
  // ============================================================================

  /**
   * Generate AI-powered test data for an API component's DataMatrix.
   * Extracts API parameters and populates the DataMatrix with LLM-suggested values.
   */
  async generateApiTestData(
    request: ApiTestDataGenerationRequest
  ): Promise<ApiTestDataGenerationResponse> {
    try {
      logger.info('AI_SERVICE', 'Generating API test data', {
        componentId: request.componentId,
        categories: request.categories
      });

      const response = await this.api.post<ApiTestDataGenerationResponse>(
        '/api/ai/api-test-data/generate',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'API test data generated', {
          columns: (response as any).columnsCreated,
          rows: (response as any).rowsGenerated
        });
        return response as unknown as ApiTestDataGenerationResponse;
      }

      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to generate API test data',
        columnsCreated: 0,
        rowsGenerated: 0,
        fieldNames: [],
        validationColumnsCreated: 0,
        warnings: []
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'API test data generation failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        columnsCreated: 0,
        rowsGenerated: 0,
        fieldNames: [],
        validationColumnsCreated: 0,
        warnings: []
      };
    }
  }

  /**
   * Phase 2: Refine validation columns using a sample API response.
   * Sends the response body to the LLM for analysis, then updates
   * Expected.bodyContains/bodyNotContains columns with smarter values.
   * This is opt-in — the user explicitly chooses to send the response to the LLM.
   */
  async refineValidations(request: RefineValidationsRequest): Promise<RefineValidationsResponse> {
    try {
      logger.info('AI_SERVICE', 'Refining validations with sample response', {
        componentId: request.componentId,
        responseLength: request.sampleResponseBody?.length
      });

      const response = await this.api.post<RefineValidationsResponse>(
        '/api/ai/api-test-data/refine-validations',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Validations refined', {
          columnsUpdated: (response as any).columnsUpdated,
          assertions: (response as any).assertions?.length
        });
        return response as unknown as RefineValidationsResponse;
      }

      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to refine validations',
        columnsUpdated: 0,
        rowsUpdated: 0,
        assertions: [],
        warnings: []
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Refine validations failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        columnsUpdated: 0,
        rowsUpdated: 0,
        assertions: [],
        warnings: []
      };
    }
  }

  // ============================================================================
  // Selector Disambiguation
  // ============================================================================

  /**
   * Disambiguate selector when multiple page elements share the same label.
   * Sends candidates to LLM to pick the best locator for the given context.
   */
  async disambiguateSelector(
    request: SelectorDisambiguationRequest
  ): Promise<SelectorDisambiguationResponse> {
    try {
      logger.info('AI_SERVICE', 'Disambiguating selector', {
        label: request.elementLabel,
        candidateCount: request.candidates.length
      });

      const response = await this.api.post<SelectorDisambiguationResponse>(
        '/api/ai/selector/disambiguate',
        request
      );

      if (response && (response as any).success !== false) {
        logger.info('AI_SERVICE', 'Selector disambiguated', {
          selectedIndex: (response as any).selectedIndex,
          strategy: (response as any).recommendedStrategy,
          confidence: (response as any).confidence
        });
        return response as unknown as SelectorDisambiguationResponse;
      }

      return {
        success: false,
        errorMessage: (response as any)?.errorMessage || 'Failed to disambiguate selector',
        selectedIndex: -1,
        recommendedStrategy: '',
        recommendedSelector: '',
        confidence: 0
      };
    } catch (error) {
      logger.error('AI_SERVICE', 'Selector disambiguation failed', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        selectedIndex: -1,
        recommendedStrategy: '',
        recommendedSelector: '',
        confidence: 0
      };
    }
  }

  // ============================================================================
  // Label Enhancement (Repo Tab AI Label Improvement)
  // ============================================================================

  /**
   * Convert a PageElement (from Repo tab) to WebElement format for the AI service.
   * PageElement has nested identifiers; WebElement has flat fields.
   */
  static convertPageElementToWebElement(pe: any): WebElement {
    const identifiers = pe.identifiers || {};
    return {
      tagName: pe.tagName || '',
      elementId: identifiers.id || undefined,
      classNames: identifiers.className ? identifiers.className.split(/\s+/).filter(Boolean) : [],
      xpath: identifiers.xpath || '',
      cssSelector: identifiers.css || '',
      relativeXpath: identifiers.rxPath || undefined,
      attributes: {
        name: identifiers.name || '',
        id: identifiers.id || '',
        type: pe.elementType || '',
        placeholder: pe.placeholder || '',
      },
      textContent: identifiers.labelText || pe.label || undefined,
      inputType: pe.elementType || undefined,
      isVisible: true,
      isEnabled: true,
      isInteractive: true,
      // Additional context fields for label enhancement
      currentLabel: pe.label || undefined,
      action: pe.action || undefined,
      inputDataType: pe.inputDataType || undefined,
      mandatory: pe.mandatory || undefined,
      source: pe.source || undefined,
    };
  }

  /**
   * Enhance page element labels using AI.
   * Accepts PageElement[] from the Repo tab, maps to WebElement[], calls the AI service,
   * and returns enhanced labels with quality indicators.
   */
  async enhancePageElementLabels(
    url: string,
    title: string,
    elements: any[]
  ): Promise<EnhancedLabelResult> {
    try {
      const webElements = elements.map(AIService.convertPageElementToWebElement);
      const pageContext: PageContext = { url, title, elements: webElements };

      const response = await this.analyzePageElements(pageContext);

      if (!response || response.success === false) {
        return {
          success: false,
          analyses: [],
          llmEnhancedCount: 0,
          analysisMethod: 'none',
          error: response?.error || 'AI service returned no data',
        };
      }

      const analyses: ElementAnalysisResult[] = (response.element_analyses || []).map((a: any) => ({
        element_xpath: a.element_xpath || '',
        suggested_name: a.suggested_name || '',
        category: a.category || '',
        llm_used: a.llm_used === true,
      }));

      return {
        success: true,
        analyses,
        llmEnhancedCount: response.metadata?.llm_enhanced_count || 0,
        analysisMethod: response.metadata?.analysis_method || 'rule_based',
      };
    } catch (error: any) {
      console.error('[AIService] enhancePageElementLabels failed:', error);
      return {
        success: false,
        analyses: [],
        llmEnhancedCount: 0,
        analysisMethod: 'none',
        error: error.message || 'Unknown error',
      };
    }
  }

  // ============================================================================
  // Test Plan Generation (AI Generate Test Plan from Description)
  // ============================================================================

  /**
   * Generate a test plan from a plain-English description (preview only, no persistence).
   */
  async generateTestPlan(request: TestPlanGenerationRequest): Promise<TestPlanGenerationResponse> {
    try {
      logger.info('AI_SERVICE', 'Generating test plan', { description: request.description.substring(0, 80) });

      const response = await this.api.post<TestPlanGenerationResponse>(
        '/api/ai/test-plan-generation/generate',
        request
      );

      if (response) {
        logger.info('AI_SERVICE', 'Test plan generated', {
          suites: response.suites?.length,
          totalCases: response.suites?.reduce((sum, s) => sum + (s.test_cases?.length || 0), 0)
        });
        return response;
      }

      return {
        request_id: '',
        project_key: request.project_key,
        plan_name: '',
        plan_description: '',
        suites: [],
        success: false,
        error_message: 'Empty response from AI service',
      };
    } catch (error: any) {
      logger.error('AI_SERVICE', 'Test plan generation failed', { error: error.message });
      return {
        request_id: '',
        project_key: request.project_key,
        plan_name: '',
        plan_description: '',
        suites: [],
        success: false,
        error_message: error.message || 'Unknown error',
      };
    }
  }

  /**
   * Save a generated (and user-reviewed) test plan to TCM entities.
   * Creates TestPlan + TestSuites + TestCases in one operation.
   */
  async saveGeneratedTestPlan(request: SaveTestPlanRequest): Promise<SaveTestPlanResponse> {
    try {
      logger.info('AI_SERVICE', 'Saving generated test plan', { planName: request.plan_name });

      const response = await this.api.post<SaveTestPlanResponse>(
        '/api/ai/test-plan-generation/save',
        request
      );

      if (response) {
        logger.info('AI_SERVICE', 'Test plan saved', {
          testPlanId: response.testPlanId,
          suiteCount: response.suiteCount,
          testCaseCount: response.testCaseCount
        });
        return response;
      }

      return { success: false, errorMessage: 'Empty response from server' };
    } catch (error: any) {
      logger.error('AI_SERVICE', 'Test plan save failed', { error: error.message });
      return { success: false, errorMessage: error.message || 'Unknown error' };
    }
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Convert browser extension captured element to WebElement
   */
  static convertCapturedElement(element: any): WebElement {
    return {
      tagName: element.tagName || element.tag || '',
      elementId: element.id || element.elementId || undefined,
      classNames: element.classNames || element.classList || [],
      xpath: element.xpath || element.xPath || '',
      cssSelector: element.cssSelector || element.cssPath || '',
      relativeXpath: element.relativeXpath || element.rXpath || undefined,
      attributes: element.attributes || {},
      textContent: element.textContent || element.innerText || undefined,
      innerHTML: element.innerHTML || undefined,
      inputType: element.type || element.inputType || undefined,
      isVisible: element.isVisible !== false,
      isEnabled: element.isEnabled !== false && !element.disabled,
      isInteractive: element.isInteractive ||
        ['input', 'button', 'select', 'textarea', 'a'].includes((element.tagName || '').toLowerCase())
    };
  }

  /**
   * Create PageContext from browser extension data
   */
  static createPageContext(
    url: string,
    title: string,
    elements: any[],
    screenshots?: string[]
  ): PageContext {
    return {
      url,
      title,
      elements: elements.map(AIService.convertCapturedElement),
      screenshots
    };
  }

  /**
   * Clear status cache
   */
  clearCache(): void {
    this.cachedStatus = null;
    this.statusCacheTime = 0;
  }
}

// Export singleton instance
export const aiService = AIService.getInstance();
export default AIService;
